import {
  abortCsvExport,
  checkDownloadStatus,
  createDownloadRequest,
} from '~/api/downloads';
import type { DownloadFiltersType } from '~/types/api';
import { resultOf } from '~/utils/fp-results';

const POLL_INTERVAL_MS = 5000; // 5 seconds
const MAX_POLL_ATTEMPTS = 360; // 360 tries × 5 seconds = 30 minutes

/**
 * Triggers a programmatic file download from a URL
 */
const triggerFileDownload = (fileUrl: string): void => {
  const link = document.createElement('a');
  link.setAttribute('href', fileUrl);
  link.setAttribute('download', ''); // Let browser determine filename from URL
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Initiates CSV download with polling mechanism
 * @param filters - Loan filters (excluding page and limit)
 * @param onCleanup - Optional callback to receive cleanup function
 * @returns Promise that resolves when download completes successfully, rejects on error
 */
export const initiateCSVDownload = (
  filters: DownloadFiltersType,
  onCleanup?: (cleanup: () => void) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    let pollAttempts = 0;
    let pollIntervalId: NodeJS.Timeout | null = null;
    let isCleanedUp = false;
    let requestId: string | null = null;

    const handleBeforeUnload = (): void => {
      cleanup();
    };

    const cleanup = (isRequestTerminated = false): void => {
      if (isCleanedUp) {
        return;
      }
      isCleanedUp = true;

      // Remove beforeunload listener
      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (pollIntervalId !== null) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
      if (isRequestTerminated) {
        requestId = null;
      }
      // Abort the download on backend if requestId exists
      if (requestId) {
        // Fire and forget - don't wait for abort to complete
        // Ignore the result of the abort request
        resultOf(abortCsvExport(requestId));
        reject(new Error('Download was cancelled.'));
      }
    };

    // Add beforeunload listener to abort download when page is closing
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Expose cleanup function if callback provided
    onCleanup?.(cleanup);

    const startPolling = async (requestId: string): Promise<void> => {
      const poll = async (): Promise<void> => {
        if (isCleanedUp) {
          return;
        }

        pollAttempts += 1;

        // Check timeout
        if (pollAttempts > MAX_POLL_ATTEMPTS) {
          cleanup();
          reject(
            new Error('Download request timed out. Please try again later.'),
          );
          return;
        }

        const [ok, response] = await resultOf(checkDownloadStatus(requestId));

        if (isCleanedUp) {
          return;
        }

        if (!ok || !response.success) {
          cleanup(true);
          reject(
            new Error('An error occurred while checking download status.'),
          );
          return;
        }

        const fileDetails = response.data.data;

        if (fileDetails.status === 'COMPLETED') {
          cleanup(true);
          if (fileDetails.downloadUrl) {
            triggerFileDownload(fileDetails.downloadUrl);
            resolve();
          } else {
            reject(new Error('Download completed but file URL is missing.'));
          }
          return;
        }

        if (
          fileDetails.status !== 'PENDING' &&
          fileDetails.status !== 'PROCESSING'
        ) {
          // if something went wrong, request is no longer active
          cleanup(true);
          reject(new Error('Download request failed. Please try again.'));
          return;
        }
        // Status is "PENDING" or "PROCESSING", continue polling
      };

      // Start polling immediately, then set interval
      await poll();
      if (!isCleanedUp) {
        pollIntervalId = setInterval(poll, POLL_INTERVAL_MS);
      }
    };

    // Initiate download request
    (async () => {
      const [ok, response] = await resultOf(createDownloadRequest(filters));

      if (isCleanedUp) {
        return;
      }

      if (!ok || !response.success) {
        cleanup();
        reject(new Error('An error occurred while creating download request.'));
        return;
      }

      requestId = response.data.data.requestId;
      startPolling(requestId);
    })();
  });
};
